import React, { useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ShowFeedModal } from '../../../../components/DataFeedWizard/ShowFeedModal/ShowFeedModal';
import StyledDataGrid from '../../../../components/DataGrid/DataGridStyles';
import { SearchBar } from '../../../SearchBar/SearchBar';
import { IllioSearchCell } from '../../../Infra/CustomAutocomplete/IllioSearchCell';
import { ModifiedIndicator } from '../../../Infra/ModifiedIndicator';
import { StatusIndicator } from '../../../Infra/StatusIndicator';
import { useLinkInstrumentsStep } from './useLinkInstrumentsStep';
import { LinkInstrumentsStepProps } from './types';
import { StepMetadata } from '../../../../pages/DataFeedWizard/types';
import {
  InstrumentMatchItem,
  InstrumentSourceId,
} from '../../../../types/generatedTypes';
import { getNumericStringSortComparator } from '../../../../utils/sortComparators';
import styles from './LinkInstrumentsStep.module.scss';

export const LinkInstrumentsStep: React.FC<
  LinkInstrumentsStepProps
> = props => {
  const {
    rows,
    getAvailableOptionsForRow,
    handleIllioInstrumentChange,
    handleSearch,
    searchLoadingRows,
    handleMoveToOTC,
    handleRowUpdate,
    tableSearchTerm,
    handleTableSearch,
    isItemDisabledAccountOnly,
    prices,
  } = useLinkInstrumentsStep();

  const [feedModalOpen, setFeedModalOpen] = useState(false);
  const [feedFilter, setFeedFilter] = useState<{
    symbol?: string | null;
    isin?: string | null;
  }>({});

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'status',
        headerName: '',
        width: 40,
        minWidth: 40,
        align: 'center',
        sortable: true,
        renderCell: (params: GridRenderCellParams<InstrumentMatchItem>) => (
          <StatusIndicator status={params.row.status} />
        ),
      },
      {
        field: 'feed_info',
        headerName: '',
        width: 60,
        minWidth: 60,
        sortable: false,
        align: 'center',
        renderCell: (params: GridRenderCellParams<InstrumentMatchItem>) => {
          if (!params.row.symbol && !params.row.isin) return null;
          return (
            <Tooltip title="View feed transactions">
              <IconButton
                size="small"
                onClick={() => {
                  setFeedFilter({
                    symbol: params.row.symbol,
                    isin: params.row.isin,
                  });
                  setFeedModalOpen(true);
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: 'instrument_type',
        headerName: 'Type',
        width: 180,
        sortable: true,
        renderCell: (params: GridRenderCellParams<InstrumentMatchItem>) => {
          const isDisabled = isItemDisabledAccountOnly(params.row.account_numbers);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
              {params.value}
              {isDisabled && (
                <Tooltip title="This instrument appears only in disabled accounts">
                  <Chip label="Disabled" size="small"
                    sx={{ backgroundColor: '#fff8e1', color: '#f9a825', border: '1px solid #ffe082', fontSize: '0.7rem', height: 20 }} />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: 'instrument_source_id',
        headerName: 'Illio Instrument',
        flex: 1,
        minWidth: 480,
        sortable: true,
        // Sort by row.name (displayed instrument name) since instrument_source_id is an object
        valueGetter: (
          _value: InstrumentSourceId | null | undefined,
          row: InstrumentMatchItem
        ) => row.name || '',
        getSortComparator: getNumericStringSortComparator,
        renderCell: (params: GridRenderCellParams<InstrumentMatchItem>) => {
          const options = getAvailableOptionsForRow(params.row.id);
          const selectedOption =
            options.find(
              opt => opt.id === params.row.instrument_source_id?.source_id
            ) || null;

          return (
            <IllioSearchCell
              rowId={params.row.id}
              selectedOption={selectedOption}
              options={options}
              loading={searchLoadingRows[params.row.id] || false}
              onSearch={handleSearch}
              onChange={(rowId, value) =>
                handleIllioInstrumentChange(
                  rowId,
                  value
                    ? { source_id: value.id, source_data: value.source_data }
                    : null,
                  value?.name || null,
                  value?.code || null,
                  value?.currency_code || null,
                  value?.label || null,
                  value?.original_multiplier ?? null,
                  value?.instrument_type || null
                )
              }
              placeholder="Search or select instrument..."
              className={styles.illioInstrumentAutocomplete}
            />
          );
        },
      },
      {
        field: 'isin',
        headerName: 'ISIN',
        width: 180,
        sortable: true,
        renderCell: (params: GridRenderCellParams<InstrumentMatchItem>) =>
          params.value || '',
      },
      {
        field: 'symbol',
        headerName: 'Symbol',
        width: 120,
        sortable: true,
        renderCell: (params: GridRenderCellParams<InstrumentMatchItem>) =>
          params.value || '',
      },
      {
        field: 'price',
        headerName: 'Price',
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams<InstrumentMatchItem>) => {
          if (!params.row.instrument_source_id?.source_id) return '';
          const priceInfo = prices[params.row.id];
          if (!priceInfo) return '';
          if (priceInfo.loading) return <CircularProgress size={16} />;
          if (priceInfo.error || priceInfo.value === null)
            return (
              <span style={{ color: '#d32f2f', fontSize: '0.8rem' }}>
                PRICE UNAVAILABLE
              </span>
            );
          return priceInfo.value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
          });
        },
      },
      {
        field: 'currency_code',
        headerName: 'Currency',
        width: 100,
        sortable: true,
        renderCell: (params: GridRenderCellParams<InstrumentMatchItem>) =>
          params.row.instrument_source_id?.source_id
            ? params.row.currency_code
            : '',
      },
      {
        field: 'multiplier',
        headerName: 'Multiplier',
        width: 120,
        editable: true,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        sortable: true,
        valueSetter: (value, row) => {
          if (value === 0) {
            return row;
          }
          return { ...row, multiplier: value };
        },
        renderCell: (params: GridRenderCellParams<InstrumentMatchItem>) => {
          if (!params.row.instrument_source_id?.source_id) {
            return '';
          }
          const isModified =
            params.row.original_multiplier != null &&
            params.row.multiplier !== params.row.original_multiplier;
          return (
            <ModifiedIndicator
              isModified={isModified}
              value={params.value ?? 1}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 140,
        sortable: false,
        renderCell: (params: GridRenderCellParams<InstrumentMatchItem>) => {
          return (
            <Tooltip title="Classify this security as OTC">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleMoveToOTC(params.row.id)}
                sx={{ '&:hover': { backgroundColor: 'transparent' } }}
              >
                <ArrowForwardIcon fontSize="small" />
                <span style={{ marginLeft: 4, fontSize: '0.75rem' }}>
                  Move to OTC
                </span>
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [
      getAvailableOptionsForRow,
      handleIllioInstrumentChange,
      handleSearch,
      searchLoadingRows,
      handleMoveToOTC,
      isItemDisabledAccountOnly,
      prices,
    ]
  );

  return (
    <Box className={styles.container}>
      <Box className={styles.section}>
        <Box className={styles.controlsRow}>
          <SearchBar
            onSearch={handleTableSearch}
            value={tableSearchTerm}
            placeholder="Search instruments..."
          />
        </Box>
        <Box className={styles.gridContainer}>
          <StyledDataGrid
            rows={rows}
            columns={columns}
            getRowId={row => row.id}
            disableColumnMenu
            disableRowSelectionOnClick
            hideFooter
            density="standard"
            showCellVerticalBorder
            processRowUpdate={handleRowUpdate}
            onProcessRowUpdateError={error =>
              console.error('Row update error:', error)
            }
          />
        </Box>
      </Box>
      {feedModalOpen && (
        <ShowFeedModal
          open={feedModalOpen}
          onClose={() => setFeedModalOpen(false)}
          filterBy={feedFilter}
        />
      )}
    </Box>
  );
};

export const stepMetadata: StepMetadata = {
  title: 'Link ISIN/Symbol to Instruments',
  subtitle:
    'Match securities to existing instruments or move unmatched items to OTC',
  icon: <ShowChartIcon />,
};
